import * as React from "react";

import Select from "react-select";

import { newSetKeyValueChange } from "../../../../helpers/Doc/changes";

type Options = Array<string> | Array<TLabelValueOption>;

interface DocEditHeaderSelectInputProps {
  contentKey: string;
  defaultValue: string;
  disableMetadataEdit: boolean;
  editingContext: EditingContextInEditor;
  label: string;
  options: Options;
  uid: string;
  value: string;
  useMenuPortal?: boolean;
  classNamePrefix?: string;
}

function isStringArray(arr: Options): arr is Array<string> {
  return arr.length > 0 && typeof arr[0] === "string";
}

export default function DocEditHeaderSelectInput(
  props: DocEditHeaderSelectInputProps
) {
  const {
    contentKey,
    defaultValue,
    disableMetadataEdit,
    editingContext,
    label,
    options,
    uid,
    value,
    useMenuPortal = false,
    classNamePrefix = '',
  } = props;

  const selectOptions = isStringArray(options)
    ? options.map((opt) => ({ value: opt, label: opt }))
    : options;
  const defaultOptionValue =
    value || defaultValue || (selectOptions[0]?.value ?? "");

  // --- Prevent scroll jump on select open ---
  const scrollPosRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);

  // Find the closest scrollable container with the class
  function getScrollContainer(): HTMLElement | null {
    if (!containerRef.current) return null;
    let el: HTMLElement | null = containerRef.current;
    while (el && !el.classList.contains('lab-values-table-scroll-container')) {
      el = el.parentElement;
    }
    return el;
  }

  function handleMenuOpen() {
    if (!useMenuPortal) return;

    const scrollContainer = getScrollContainer();
    if (scrollContainer) {
      scrollPosRef.current = scrollContainer.scrollTop;
    }
    setMenuIsOpen(true);
  }

  function handleMenuClose() {
    if (!useMenuPortal) return;

    const scrollContainer = getScrollContainer();
    if (scrollContainer && scrollPosRef.current !== null) {
      scrollContainer.scrollTop = scrollPosRef.current;
    }
    setMenuIsOpen(false);
  }

  const menuPortalProperties = useMenuPortal
    ? {
        menuPortalTarget: typeof window !== 'undefined' ? window.document.body : undefined,
        menuPosition: 'fixed' as const,
      }
    : {};

  // Restore scroll position only once after menu opens
  React.useLayoutEffect(() => {
    if (!menuIsOpen) return;
    const scrollContainer = getScrollContainer();
    if (!scrollContainer || scrollPosRef.current == null) return;
    // Restore after a short delay to allow react-select to do its scroll
    const timeout = setTimeout(() => {
      scrollContainer.scrollTop = scrollPosRef.current!;
    }, 0);
    return () => clearTimeout(timeout);
  }, [menuIsOpen]);

  return (
    <div
      className="doc-edit-header-input doc-edit-header-select-input"
      data-can-not-contain-doc-selection
      ref={containerRef}
    >
      <label>{label}</label>
      <div>
        <Select
          defaultValue={selectOptions.find(
            (option) => defaultOptionValue === option.value
          )}
          options={selectOptions}
          value={selectOptions.find((option) => value === option.value)}
          isDisabled={disableMetadataEdit}
          isSearchable
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          {...menuPortalProperties}
          onChange={(selectedOption) => {
            editingContext.addChange(
              newSetKeyValueChange(uid, contentKey, selectedOption.value)
            );
          }}
          classNamePrefix={classNamePrefix}
        />
      </div>
    </div>
  );
}
